// The page of a news, presentational: the frame of the News area, «Torna
// alle news», the head of the card (chips, date line), the title, the
// metadata, the notices, the actions, the attachments, the iter and the
// text in chunks. Every element is the one the design already has:
//   frame            NewsLayout.tsx:24-25 (the collapsed layout)
//   «Torna alle news» an action button of the card without its icon, as a link (ACTION_LINK_CLASS, actions.ts)
//   head, bookmark   NewsCard.tsx:126-139 and 157-165
//   title            the section's heading, NewsSection.tsx:38
//   metadata         the date line of the card, NewsCard.tsx:137
//   section labels   «Fonti» of the feed editor, FeedEditor.tsx:80
//   rows             «Fonti eliminate», panels.tsx:231-238 (no strike-through, no tree indent)
//   «Apri»           the status button, PanelStatus.tsx:4-5
//   text             the card's paragraphs, NewsCard.tsx:102-107
//   «Continua a leggere» «Carica altre», ListStatus.tsx:30-38
//   with a sidebar   the frame of the lists, NewsLayout.tsx (the Parliament's index beside the text, closable as every panel)
// The data comes ready from the page (src/pages/NewsItemPage.tsx): nothing
// here calls the api.
import { Button, buttonVariants } from "@heroui/react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";

import type { IterRow, NewsItem, ReaderChunk, ReaderFile } from "../../data/mock";
import { ACTION_LINK_CLASS } from "../actions";
import { Icon } from "../Icon";
import { IngestionIcon } from "../IngestionIcon";
import { NewsLayout, type SidePanel } from "../NewsLayout";
import { PanelStatus, STATUS_BUTTON_CLASS } from "../PanelStatus";
import { SourceChip } from "./NewsCard";
import { SourceAvatar } from "./SourceAvatar";

export const BACK_TO_NEWS = "Torna alle news";
export const CONTINUE_READING = "Continua a leggere";
const TITLE_ID = "news-title";
/** How far below the viewport the next chunk is asked for, so the reader never shows its end. */
const SENTINEL_MARGIN = "200px";

const integer = new Intl.NumberFormat("it-IT");

export type NewsReaderProps = {
  news: NewsItem;
  /** Where «Torna alle news» leads. */
  backTo: string;
  /** The location state it leads with: the way back of the page it returns to (a sitting's). */
  backState?: { from: string };
  /** Its label, when not «Torna alle news» («Torna alla seduta»). */
  backLabel?: string;
  onToggleSaved: () => void;
  /** A bookmark the api refused: the message and how to send it again. */
  saveFailure?: { message: string; onRetry: () => void } | null;
  /** item_type, doc_number, …: only the ones there, joined by « · ». */
  metadata: string[];
  /** Neutral notices above the text (the text is partial, missing, a file to read by hand). */
  notices: string[];
  /** The row of actions, under the head and before the text. */
  actions: ReactNode;
  files: ReaderFile[];
  iter: IterRow[];
  chunks: ReaderChunk[];
  totalChars: number;
  /** What an origin adds (Parliament's own section, in its chapter): after the iter, before the text. */
  extra?: ReactNode;
  /** The text as an origin's chapter renders it (the Parliament's reader, cut at its index), instead of the chunks. */
  reader?: ReactNode;
  /** A side panel (the Parliament's index, with its title): the page takes the frame of the lists, NewsLayout, where the panel closes. */
  sidebar?: SidePanel;
  /** False when the page opens at an entry of the text (`?voce=`): the focus goes there, not to the title. */
  focusTitle?: boolean;
};

/**
 * The frame of the page: the News area's white panel, with «Torna alle news»
 * first. Used by every state of the page. With a `sidebar`, the frame of the
 * lists (NewsLayout): the panel on the left, the white panel on the right.
 */
export function NewsReaderFrame({
  backTo,
  backState,
  backLabel = BACK_TO_NEWS,
  labelledBy,
  label,
  sidebar,
  children,
}: {
  backTo: string;
  /** The location state of the way back: the way back of the page it returns to (a sitting's «Torna alle sedute»). */
  backState?: { from: string };
  /** «Torna alle news», or «Torna alla seduta» when the page was opened from a sitting of the Parliament. */
  backLabel?: string;
  labelledBy?: string;
  label?: string;
  sidebar?: SidePanel;
  children: ReactNode;
}) {
  const back = (
    <div className="flex items-center justify-between gap-2.5">
      <Link to={backTo} state={backState} className={ACTION_LINK_CLASS}>
        {backLabel}
      </Link>
    </div>
  );
  if (sidebar) {
    return (
      <NewsLayout sidebar={sidebar} labelledBy={labelledBy}>
        <div className="flex flex-col gap-[25px]">
          {back}
          {children}
        </div>
      </NewsLayout>
    );
  }
  return (
    <div className="mt-2.5 flex flex-1">
      <main
        aria-labelledby={labelledBy}
        aria-label={label}
        className="flex min-w-0 flex-1 justify-center bg-white px-6 pt-2 pb-12 lg:px-[93px]"
      >
        <div className="flex w-full max-w-[1063px] flex-col gap-[49px]">
          <div className="flex flex-col gap-[25px]">
            {back}
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}

/** A label of a section of the page («Allegati», «Iter», «Parlamento»). */
export function SectionLabel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2 id={id} className="text-sm leading-6 font-medium text-accent">
      {children}
    </h2>
  );
}

/** A row of a section of the page: the line of «Fonti eliminate» (panels.tsx), without its indent. */
export const ROW_CLASS = "py-2 text-sm leading-6 text-muted";

function Files({ files }: { files: ReaderFile[] }) {
  return (
    <section aria-labelledby="news-files" className="flex flex-col">
      <SectionLabel id="news-files">Allegati</SectionLabel>
      <ul>
        {files.map((file) => (
          <li key={file.id} className={`flex items-center justify-between gap-2.5 ${ROW_CLASS}`}>
            <span className="min-w-0 break-words">
              {[file.name, file.kind, file.size, file.status].filter(Boolean).join(" · ")}
            </span>
            {/* a plain link to the api's download route: the browser follows its 302 to the file */}
            <a
              href={file.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className={`${buttonVariants({ size: "sm" })} shrink-0 ${STATUS_BUTTON_CLASS}`}
            >
              Apri
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Iter({ iter }: { iter: IterRow[] }) {
  return (
    <section aria-labelledby="news-iter" className="flex flex-col">
      <SectionLabel id="news-iter">Iter</SectionLabel>
      <ol>
        {iter.map((row, i) => (
          <li key={i} className={`${ROW_CLASS} ${row.current ? "font-medium text-foreground" : ""}`}>
            {row.text}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * The text, one chunk at a time: the next one comes when the end of the
 * text shown enters the viewport (an IntersectionObserver on a sentinel,
 * disconnected on unmount) and always with «Continua a leggere», from the
 * keyboard too. The DOM holds only the chunks shown so far. A text of one
 * chunk has no controls.
 */
function Text({ chunks, totalChars }: { chunks: ReaderChunk[]; totalChars: number }) {
  const [shown, setShown] = useState(1);
  const sentinel = useRef<HTMLDivElement>(null);
  const hasMore = shown < chunks.length;

  useEffect(() => {
    const target = sentinel.current;
    if (!hasMore || !target || typeof IntersectionObserver !== "function") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setShown((n) => Math.min(n + 1, chunks.length));
      },
      { rootMargin: SENTINEL_MARGIN },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, shown, chunks.length]);

  const shownChars = chunks.slice(0, shown).reduce((sum, chunk) => sum + chunk.chars, 0);

  return (
    <section aria-label="Testo" className="flex flex-col gap-[15px]">
      <div className="text-xs leading-5 text-muted">
        {chunks.slice(0, shown).map((chunk, c) =>
          chunk.blocks.map((block, b) => (
            <p key={`${c}-${b}`} className="min-h-5">
              {block}
            </p>
          )),
        )}
      </div>
      {chunks.length > 1 && (
        <>
          <div ref={sentinel} aria-hidden />
          <p aria-live="polite" className="text-xs leading-5 text-muted">
            Mostrati {integer.format(shownChars)} di {integer.format(totalChars)} caratteri
          </p>
          {hasMore && (
            <div className="flex items-center justify-end">
              <Button size="sm" onPress={() => setShown((n) => n + 1)} className={STATUS_BUTTON_CLASS}>
                {CONTINUE_READING}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function NewsReader({
  news,
  backTo,
  backState,
  backLabel,
  onToggleSaved,
  saveFailure = null,
  metadata,
  notices,
  actions,
  files,
  iter,
  chunks,
  totalChars,
  extra,
  reader,
  sidebar,
  focusTitle = true,
}: NewsReaderProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  // the focus lands on the title when the page opens: the reading starts there (unless the page opens at an
  // entry of the text, `focusTitle` false: the reader puts the focus on that entry)
  useEffect(() => {
    if (focusTitle) heading.current?.focus();
  }, [focusTitle]);

  return (
    <NewsReaderFrame
      backTo={backTo}
      backState={backState}
      backLabel={backLabel}
      labelledBy={TITLE_ID}
      sidebar={sidebar}
    >
      {saveFailure && (
        <PanelStatus role="alert" indent={false} onRetry={saveFailure.onRetry}>
          {saveFailure.message}
        </PanelStatus>
      )}
      <div className="flex w-full gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-[18px]">
          <div className="flex flex-col gap-[13px]">
            <div className="flex items-center gap-[9px]">
              <SourceChip {...news.source} />
              <SourceChip {...news.channel} />
              {news.hasCode && <IngestionIcon />}
            </div>
            <div className="flex items-center gap-[13px]">
              <SourceAvatar logo={news.source.logo} initials={news.source.initials} />
              <p className="text-xs leading-5 font-medium">
                <span className="block text-black">{news.channel.name}</span>
                <span className="block text-muted">{news.publishedLabel}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <h1 id={TITLE_ID} ref={heading} tabIndex={-1} className="text-xl leading-7 font-semibold text-brand outline-none">
              {news.title}
            </h1>
            {metadata.length > 0 && <p className="text-xs leading-5 text-muted">{metadata.join(" · ")}</p>}
            {notices.map((notice) => (
              <PanelStatus key={notice} role="note" indent={false}>
                {notice}
              </PanelStatus>
            ))}
          </div>
        </div>
        <div className="flex w-4 shrink-0 flex-col items-start">
          <button
            type="button"
            onClick={onToggleSaved}
            aria-pressed={news.saved}
            aria-label={news.saved ? "Rimuovi dalle news salvate" : "Salva news"}
            className="cursor-pointer rounded-sm"
          >
            <Icon name={news.saved ? "bookmark" : "bookmarkOutline"} />
          </button>
        </div>
      </div>
      {actions}
      {files.length > 0 && <Files files={files} />}
      {iter.length > 0 && <Iter iter={iter} />}
      {extra}
      {reader ?? (chunks.length > 0 && <Text chunks={chunks} totalChars={totalChars} />)}
    </NewsReaderFrame>
  );
}
